// TableCellEditor.jsx

import React from 'react';

// Utilities
import {
    isCurrencyInputPartial,
    parseCurrencyInputToCents,
    resolveFieldCurrencyCode,
} from 'utilities/Currency';
import { isUrlInputValid } from 'utilities/Url';

// CSS
import './TableCellEditor.css';

/**
 * TableCellEditor
 *
 * @returns {ReactNode} The rendered component.
 */
const TableCellEditor = ({
    fieldType,
    fieldDef = null,
    value,
    onChange,
    onCommit,
    onCancel,
}) => {

    // ----- CONTEXT ----- //

    const isBoolean = fieldType === 'boolean';
    const isCurrency = fieldType === 'money';
    const isUrl = fieldType === 'url';
    const currencyCode = resolveFieldCurrencyCode(fieldDef);
    const inputType = isBoolean ? 'checkbox' : (fieldType === 'number' || fieldType === 'percentage' ? 'number' : 'text');

    // ----- UTILITIES ----- //

    const commitCurrencyIfValid = () => {
        const trimmed = String(value ?? '').trim();
        if (trimmed === '') {
            onCommit();
            return;
        }

        const parsed = parseCurrencyInputToCents(trimmed, currencyCode);
        if (parsed.ok) {
            onCommit();
            return;
        }

        onCancel();
    };

    const commitUrlIfValid = () => {
        if (isUrlInputValid(value)) {
            onCommit();
            return;
        }

        onCancel();
    };

    // ----- HANDLERS ----- //

    /**
     * handleKeyDown
     *
     * Handles a key down event.
     *
     * @param {object} event - event.
     */
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (isCurrency) {
                commitCurrencyIfValid();
            } else if (isUrl) {
                commitUrlIfValid();
            } else {
                onCommit();
            }
        }
        if (event.key === 'Escape') {
            onCancel();
        }
    };

    if (isBoolean) {
        return (
            <input
              className="table-view-cell-input"
              style={{
                    marginLeft: '12px',
                }}
              type={inputType}
              checked={Boolean(value)}
              autoFocus
              onBlur={onCommit}
              onChange={(event) => onChange(event.target.checked)}
              onKeyDown={handleKeyDown}
            />
        );
    }

    // ----- RENDER ----- //

    return (
        <input
          className="table-view-cell-input"
          type={isCurrency ? 'text' : inputType}
          value={value ?? ''}
          inputMode={isCurrency ? 'decimal' : (isUrl ? 'url' : undefined)}
          autoFocus
          onBlur={() => {
                if (isCurrency) {
                    commitCurrencyIfValid();
                    return;
                }
                if (isUrl) {
                    commitUrlIfValid();
                    return;
                }
                onCommit();
            }}
          onChange={(event) => {
                const nextValue = event.target.value;
                if (isCurrency) {
                    if (isCurrencyInputPartial(nextValue, currencyCode)) {
                        onChange(nextValue);
                    }
                    return;
                }
                onChange(nextValue);
            }}
          onKeyDown={handleKeyDown}
        />
    );
};

// Dev Tools
TableCellEditor.displayName = 'TableCellEditor';

export default TableCellEditor;
